import { ExternalLink, LegacyDataTableHeaderColumn, LegacyDataTableRow, LegacyDataTableRowColumn, Tooltip } from "@octopusdeploy/design-system-components";
import type { InsightsReportResource, LeadTimeDeployment, LeadTimeTrends, TenantDeployedAgo } from "@octopusdeploy/octopus-server-client";
import { InsightsReportTenantMode } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import moment from "moment";
import { formatDurationInWords } from "~/areas/insights/dataTransformation/stringHelpers";
import InternalLink from "~/components/Navigation/InternalLink";
import DateFormatter from "~/utils/DateFormatter";
import { CommonSparklineTrends } from "./CommonSparklineTrends";
import { TrendTable } from "./TrendTable/TrendTable";
interface TrendTablesProps {
    report: InsightsReportResource;
    trends: LeadTimeTrends;
}
export function LeadTimeTrendTables({ trends, report }: TrendTablesProps) {
    return (<div>
            <CommonSparklineTrends trends={trends} valueFormatter={formatDurationInWords} dataPointFormatter={(v) => (v ? moment.duration(v).asMinutes() : 0)} metricDescription={<>
                        average <ExternalLink href="InsightsDeploymentLeadTime">lead time</ExternalLink>
                    </>}/>
            <LongestLeadTimeTable data={trends.Longest} report={report}/>
            <TenantsNotRecentlyDeployedToTable data={trends.TenantsNotRecentlyDeployedTo} report={report}/>
        </div>);
}
interface LongestLeadTimeTableProps {
    data: LeadTimeDeployment[];
    report: InsightsReportResource;
}
function LongestLeadTimeTable({ data, report }: LongestLeadTimeTableProps) {
    const showTenantColumn = report.TenantMode !== InsightsReportTenantMode.Untenanted;
    return (<TrendTable title="Releases with the longest lead time" isEmpty={data.length === 0} note={<span>
                    The releases with the longest <ExternalLink href="InsightsDeploymentLeadTime">lead time</ExternalLink> for each project, environment and tenant combination. The first 10 in descending order by lead time are shown. Lower lead time
                    indicates an increased ability to respond to change and implies less inventory per deployment, which reduces the risk of that response.
                </span>} headerContent={<LegacyDataTableRow>
                    <LegacyDataTableHeaderColumn>Project</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Environment</LegacyDataTableHeaderColumn>
                    {showTenantColumn && <LegacyDataTableHeaderColumn>Tenant</LegacyDataTableHeaderColumn>}
                    <LegacyDataTableHeaderColumn>Release</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Completed</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Lead time</LegacyDataTableHeaderColumn>
                </LegacyDataTableRow>} bodyContent={<>
                    {data.map((r) => (<LegacyDataTableRow key={r.Id}>
                            <LegacyDataTableRowColumn>
                                <InternalLink to={links.projectRootRedirect.generateUrl({ spaceId: report.SpaceId, projectSlug: r.ProjectId })}>{r.ProjectName}</InternalLink>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>{r.EnvironmentName}</LegacyDataTableRowColumn>
                            {showTenantColumn && <LegacyDataTableRowColumn>{r.TenantName ?? "Untenanted"}</LegacyDataTableRowColumn>}
                            <LegacyDataTableRowColumn>
                                <InternalLink to={links.releasePage.generateUrl({ spaceId: report.SpaceId, projectSlug: r.ProjectId, releaseVersion: r.ReleaseVersion })}>{r.ReleaseVersion}</InternalLink>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>
                                <Tooltip content={DateFormatter.dateToShortFormat(r.CompletedTime) || undefined}>
                                    <InternalLink to={links.deploymentPage.generateUrl({ spaceId: report.SpaceId, projectSlug: r.ProjectId, releaseVersion: r.ReleaseVersion, deploymentId: r.Id })}>
                                        {DateFormatter.momentAgo(r.CompletedTime)}
                                    </InternalLink>
                                </Tooltip>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>{formatDurationInWords(r.LeadTime)}</LegacyDataTableRowColumn>
                        </LegacyDataTableRow>))}
                </>}/>);
}
interface TenantsNotRecentlyDeployedToTableProps {
    data: TenantDeployedAgo[];
    report: InsightsReportResource;
}
function TenantsNotRecentlyDeployedToTable({ data, report }: TenantsNotRecentlyDeployedToTableProps) {
    if (report.TenantMode === InsightsReportTenantMode.Untenanted)
        return null;
    return (<TrendTable title="Tenants not recently deployed to" isEmpty={data.length === 0} note="The last deployment for each tenant. The first 10 in ascending order by deployment completed time are shown." headerContent={<LegacyDataTableRow>
                    <LegacyDataTableHeaderColumn>Tenant</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Release</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Completed</LegacyDataTableHeaderColumn>
                    <LegacyDataTableHeaderColumn>Time since completion</LegacyDataTableHeaderColumn>
                </LegacyDataTableRow>} bodyContent={<>
                    {data.map((r) => (<LegacyDataTableRow key={r.TenantId}>
                            <LegacyDataTableRowColumn>
                                <InternalLink to={links.tenantOverviewPage.generateUrl({ spaceId: report.SpaceId, tenantId: r.TenantId })}>{r.TenantName}</InternalLink>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>
                                <InternalLink to={links.releaseRedirect.generateUrl({ spaceId: report.SpaceId, releaseId: r.ReleaseId })}>{r.ReleaseVersion}</InternalLink>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>
                                <Tooltip content={DateFormatter.dateToShortFormat(r.CompletedTime) || undefined}>
                                    <InternalLink to={links.deploymentToProjectTaskRedirect.generateUrl({ spaceId: report.SpaceId, deploymentId: r.Id })}>{DateFormatter.momentAgo(r.CompletedTime)}</InternalLink>
                                </Tooltip>
                            </LegacyDataTableRowColumn>
                            <LegacyDataTableRowColumn>{formatDurationInWords(r.CompletedAgo)}</LegacyDataTableRowColumn>
                        </LegacyDataTableRow>))}
                </>}/>);
}
